import { useEffect, useRef, useState } from 'react';
import { Award, Building2, Users, TrendingUp } from 'lucide-react';

const trustData = [
  {
    icon: TrendingUp,
    value: '1000万+',
    label: '累计销量',
    description: '服务千万钓友'
  },
  {
    icon: Award,
    value: '10+',
    label: '年行业经验',
    description: '匠心传承'
  },
  {
    icon: Users,
    value: '150+',
    label: '专业技工',
    description: '技艺精湛'
  },
  {
    icon: Building2,
    value: '超1万㎡',
    label: '生产面积',
    description: '现代化基地'
  }
];

const badges = [
  { name: '临湘浮漂百亿产业核心企业', year: '行业地位' },
  { name: '行业十大著名浮漂品牌', year: '品牌荣誉' },
  { name: '线上销量稳居前列', year: '市场认可' },
  { name: '千万钓友口碑认证', year: '用户信赖' },
  { name: '政府培训承办企业', year: '官方认可' },
  { name: '省级劳务品牌核心企业', year: '技艺实力' }
];

export default function TrustSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-gradient-to-br from-deep-lake to-deep-lake-dark relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 bg-warm-gold rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-dawn-orange rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <span
            className={`inline-block text-sm font-medium text-warm-gold tracking-wider uppercase mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Trust & Honor
          </span>
          <h2
            className={`text-3xl md:text-4xl lg:text-5xl font-serif-zh font-bold text-white mb-4 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            信任背书
          </h2>
          <p
            className={`text-lg text-white/80 max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            用数据和荣誉说话，见证品牌实力
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {trustData.map((item, index) => (
            <div
              key={item.label}
              className={`text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-500 group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${300 + index * 100}ms` }}
            >
              <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-warm-gold to-dawn-orange flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                <item.icon className="w-7 h-7 text-white" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                {item.value}
              </div>
              <div className="text-lg text-warm-gold font-medium mb-1">
                {item.label}
              </div>
              <div className="text-sm text-white/60">{item.description}</div>
            </div>
          ))}
        </div>

        <div
          className={`bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 transition-all duration-700 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h3 className="text-xl font-serif-zh font-bold text-white mb-6 text-center">
            荣誉资质
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {badges.map((badge) => (
              <div
                key={badge.name}
                className="text-center p-4 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-colors duration-300"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-warm-gold/30 to-dawn-orange/30 flex items-center justify-center">
                  <Award className="w-6 h-6 text-warm-gold" />
                </div>
                <div className="text-sm text-white font-medium mb-1">{badge.name}</div>
                <div className="text-xs text-warm-gold">{badge.year}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
